/**
 * Block metadata for the editor.
 *
 * ── INJECTED, NOT IMPORTED ───────────────────────────────────────────────────────────
 *
 * The document utilities need to know which blocks exist, what they may contain, which
 * contexts they belong to, which are critical and what their defaults are. All of that
 * already exists in the engine's manifests, which the editor fetches from
 * /api/admin/cms/blocks — and which scripts/verify-cms-registry.mjs keeps in step with
 * the compiled React registry.
 *
 * Taking it from there rather than importing the registry does two things: the utilities
 * stay free of React and can be executed directly by node, and there is no third copy of
 * "what blocks exist" to drift.
 *
 * DELIBERATELY IMPORT-FREE.
 */

export interface BlockMeta {
  id: string;
  label: string;
  version: number;
  category: string;
  contexts: string[];
  children: 'any' | 'none';
  critical: boolean;
  defaultProps: Record<string, unknown>;
  // Deliberately loose: this module is the injection point, not the consumer. The
  // editor UI reads these through the richly typed BlockManifest it fetched; the pure
  // utilities only ever ask whether a block exists, what it accepts and what is critical.
  propsSchema?: { properties?: Record<string, unknown> };
  editor?: {
    fields?: Record<string, unknown>;
    copyKeys?: { key: string; label: string }[];
    note?: string;
  };
}

let registry: Record<string, BlockMeta> = Object.create(null);

/** Called once when the editor has loaded the manifests. */
export function setBlockMetadata(blocks: BlockMeta[]): void {
  const next: Record<string, BlockMeta> = Object.create(null);
  for (const block of blocks ?? []) {
    if (!block?.id || typeof block.id !== 'string') continue;
    if (block.id === '__proto__' || block.id === 'constructor') continue;
    next[block.id] = block;
  }
  registry = next;
}

/** Own-property lookup: a document must not reach `constructor` or `toString`. */
export function blockMeta(id: unknown): BlockMeta | null {
  if (typeof id !== 'string') return null;
  return Object.hasOwn(registry, id) ? registry[id] : null;
}

export function allBlockMeta(): BlockMeta[] {
  return Object.values(registry);
}

export function isMetadataLoaded(): boolean {
  return Object.keys(registry).length > 0;
}
